import { createFileRoute, Link, Navigate, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FREE_MODELS, isAllowed, type UserRole, visibilityForRole } from "@re/shared";
import type { RunnerKind } from "../lib/api";
import { api } from "../lib/api";
import { classifyTarget } from "../lib/classify";
import { modelCostLabel, VERCEL_AI_MODELS, type VercelAiModel } from "../lib/models";
import { Badge, Button, cn, Icon, Kbd, SourceBadge, Textarea, TierBadge } from "../components/ui";

type RunnerChoice = RunnerKind | "both";

const FREE_MODEL_SET = new Set<string>(FREE_MODELS);
const DEFAULT_MODEL_KEY = "re:default-model";

function initialModel(): VercelAiModel {
  if (typeof window !== "undefined") {
    const saved = window.localStorage.getItem(DEFAULT_MODEL_KEY);
    if (saved && VERCEL_AI_MODELS.some((m) => m.id === saved)) return saved as VercelAiModel;
  }
  return "deepseek-flash";
}

export const Route = createFileRoute("/new")({ component: NewResearchPage });

function NewResearchPage() {
  const { t } = useTranslation();
  const me = useQuery({ queryKey: ["me"], queryFn: () => api.me(), retry: false });

  if (me.isLoading) {
    return <p className="py-16 text-center text-ink-faint">{t("common.loading")}</p>;
  }
  if (me.isError || !me.data) {
    return <Navigate to="/login" />;
  }
  return <NewResearchForm role={me.data.role} />;
}

function NewResearchForm({ role }: { role: UserRole }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isFree = role === "free";

  const [target, setTarget] = useState("");
  const [skipIntent, setSkipIntent] = useState(false);
  const [fresh, setFresh] = useState(false);
  const [runner, setRunner] = useState<RunnerChoice>("vercel-ai");
  const [model, setModel] = useState<VercelAiModel>(initialModel);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const inferred = target ? classifyTarget(target) : null;

  const runners = useQuery({
    queryKey: ["runners"],
    queryFn: () => api.runners(),
    staleTime: 30_000,
  });
  const capabilities = useQuery({
    queryKey: ["research-capabilities"],
    queryFn: () => api.capabilities(),
    staleTime: 60_000,
    retry: false,
  });

  const allowedModels = useMemo(
    () => (isFree ? VERCEL_AI_MODELS.filter((m) => FREE_MODEL_SET.has(m.id)) : VERCEL_AI_MODELS),
    [isFree],
  );
  const runnerEnabled = (r: RunnerChoice) => !isFree || r === "vercel-ai";

  // Keep free users within allowed runner/model combos.
  useEffect(() => {
    if (isFree && runner !== "vercel-ai") setRunner("vercel-ai");
  }, [isFree, runner]);
  useEffect(() => {
    if (isFree && !FREE_MODEL_SET.has(model)) setModel(allowedModels[0]?.id ?? "deepseek-flash");
  }, [isFree, model, allowedModels]);

  const selectedRunnerAvailable =
    runner === "both"
      ? runners.data?.claude.available !== false && runners.data?.codex.available !== false
      : runner === "vercel-ai"
        ? runners.data?.vercelAi.available !== false
        : runners.data?.[runner].available !== false;
  const selectedCapabilityAvailable =
    runner === "both"
      ? true
      : (capabilities.data?.runners.find((item) => item.id === runner)?.available ?? true);
  const canStart =
    target.trim().length > 0 &&
    !starting &&
    runners.data &&
    !runners.data.disabled &&
    selectedRunnerAvailable &&
    selectedCapabilityAvailable &&
    isAllowed(role, runner, runner === "vercel-ai" ? model : undefined);

  async function onStart() {
    if (!canStart) return;
    setStarting(true);
    setStartError(null);
    try {
      const res = await api.startResearch({
        target: target.trim(),
        runner,
        model: runner === "vercel-ai" ? model : undefined,
        yes: skipIntent,
        fresh,
      });
      const first = res.jobs[0];
      if (!first) throw new Error("no job created");
      navigate({ to: "/jobs/$id", params: { id: first.id } });
    } catch (e) {
      setStartError(e instanceof Error ? e.message : String(e));
    } finally {
      setStarting(false);
    }
  }

  const capabilityByRunner = new Map((capabilities.data?.runners ?? []).map((item) => [item.id, item.available]));
  const isRunnerAvailable = (id: RunnerChoice, fallback: boolean) => {
    if (id === "both") return fallback;
    return fallback && capabilityByRunner.get(id) !== false;
  };

  const runnerTiles: Array<{ id: RunnerChoice; label: string; sub: string; cost: string; available: boolean }> = [
    {
      id: "claude",
      label: "claude",
      sub: t("new.runnerClaudeSub"),
      cost: t("new.costSubscription"),
      available: isRunnerAvailable("claude", runnerEnabled("claude") && runners.data?.claude.available !== false),
    },
    {
      id: "codex",
      label: "codex",
      sub: t("new.runnerCodexSub"),
      cost: t("new.costSubscription"),
      available: isRunnerAvailable("codex", runnerEnabled("codex") && runners.data?.codex.available !== false),
    },
    {
      id: "vercel-ai",
      label: "vercel-ai",
      sub: t("new.runnerVercelSub"),
      cost: modelCostLabel(model),
      available: isRunnerAvailable("vercel-ai", runners.data?.vercelAi.available !== false),
    },
    {
      id: "both",
      label: "both",
      sub: t("new.runnerBothSub"),
      cost: t("new.costSubscription"),
      available: runnerEnabled("both") && runners.data?.claude.available !== false && runners.data?.codex.available !== false,
    },
  ];

  return (
    <main className="max-w-[840px] mx-auto px-6 py-14">
      <header className="mb-9">
        <div className="text-micro uppercase font-medium text-ink-faint mb-2.5" style={{ letterSpacing: "0.08em" }}>
          {t("new.eyebrow")}
        </div>
        <h1 className="font-serif text-[40px] leading-[1.1] font-medium text-ink tracking-tight">
          {t("new.title")} <span className="italic text-ink-dim">{t("new.titleEm")}</span>
        </h1>
        <p className="mt-4 text-[15px] text-ink-dim leading-relaxed max-w-[640px]">{t("new.body")}</p>
      </header>

      <div className="relative mb-7">
        <Textarea
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) onStart();
          }}
          placeholder={t("new.placeholder")}
          rows={3}
          autoFocus
          className="!text-[17px] !leading-[1.5] !p-5 !pr-32 !pb-12 !rounded-lg font-serif"
          style={{ fontFamily: "'Newsreader', serif" }}
        />
        {inferred && (
          <div className="absolute top-4 right-4">
            <SourceBadge type={inferred} size="sm" />
          </div>
        )}
        <div className="absolute bottom-3.5 left-5 right-5 flex items-center justify-between gap-4 text-[12px] text-ink-faint">
          <div className="flex items-center gap-2 font-mono">
            <Kbd>⌘</Kbd>
            <Kbd>↵</Kbd>
            <span className="font-sans">{t("new.runHint")}</span>
          </div>
          <div className="font-mono">{t("new.charCount", { count: target.length })}</div>
        </div>
      </div>

      <section className="mb-7">
        <div className="flex items-baseline justify-between gap-4 mb-3">
          <div className="text-[13px] font-medium text-ink-dim">{t("new.runnerLabel")}</div>
          {isFree && (
            <Link to="/settings" className="text-[12px] text-accent-ink hover:text-accent inline-flex items-center gap-1">
              <Icon name="sparkles" size={12} />
              {t("new.upgradeHint")}
            </Link>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {runnerTiles.map((tile) => {
            const locked = !runnerEnabled(tile.id);
            const disabled = locked || !tile.available;
            const selected = runner === tile.id && !disabled;
            return (
              <button
                key={tile.id}
                type="button"
                disabled={disabled}
                onClick={() => setRunner(tile.id)}
                className={cn(
                  "relative text-left p-3.5 rounded-md border transition-all min-h-[112px]",
                  selected
                    ? "bg-accent-soft border-accent text-ink ring-1 ring-accent"
                    : "bg-surface border-border text-ink hover:border-border-strong",
                  disabled && "opacity-55 cursor-not-allowed hover:border-border",
                )}
              >
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="text-[13.5px] font-medium font-mono truncate">{tile.label}</div>
                  {locked ? (
                    <Icon name="lock" size={12} className="text-ink-faint" />
                  ) : tile.id === "vercel-ai" ? (
                    <TierBadge tier="free" />
                  ) : (
                    <TierBadge tier="paid" />
                  )}
                </div>
                <div className="text-[11.5px] text-ink-faint leading-snug">{tile.sub}</div>
                <div className="mt-2 text-[11px] text-ink-faint font-mono">{tile.cost}</div>
                {locked && <Badge tone="accent" size="xs" className="mt-2">{t("common.pro")}</Badge>}
                {!locked && !tile.available && <div className="mt-2 text-[11px] text-danger">{t("new.unavailable")}</div>}
                {selected && (
                  <div className="absolute -top-px -right-px w-5 h-5 bg-accent rounded-bl-md rounded-tr-md flex items-center justify-center">
                    <Icon name="check" size={11} className="text-white" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {runner === "vercel-ai" && (
        <section className="mb-7">
          <div className="flex items-baseline justify-between gap-4 mb-3">
            <div className="text-[13px] font-medium text-ink-dim">{t("new.modelLabel")}</div>
            {isFree && (
              <div className="text-[11.5px] text-ink-faint">
                {t("new.freeModelNote")} ·{" "}
                <Link to="/settings" className="text-accent-ink hover:text-accent">
                  {t("new.seePricing")}
                </Link>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {VERCEL_AI_MODELS.map((m) => {
              const locked = isFree && !FREE_MODEL_SET.has(m.id);
              const selected = model === m.id && !locked;
              return (
                <button
                  key={m.id}
                  type="button"
                  disabled={locked}
                  onClick={() => setModel(m.id)}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-md border text-left transition-all",
                    selected ? "bg-accent-soft border-accent ring-1 ring-accent" : "bg-surface border-border hover:border-border-strong",
                    locked && "opacity-55 cursor-not-allowed hover:border-border",
                  )}
                >
                  <div className={cn("w-2 h-2 rounded-full shrink-0", selected ? "bg-accent" : "bg-border-strong")} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="text-[13.5px] font-medium text-ink truncate font-mono">{m.label}</div>
                      {locked ? <Icon name="lock" size={11} className="text-ink-faint" /> : <TierBadge tier={FREE_MODEL_SET.has(m.id) ? "free" : "paid"} />}
                    </div>
                    <div className="text-[11.5px] text-ink-faint mt-0.5 font-mono">{modelCostLabel(m.id)}</div>
                  </div>
                </button>
              );
            })}
          </div>
          {isFree && <p className="mt-2 text-[12px] text-ink-faint">{t("new.freeModelHint")}</p>}
        </section>
      )}

      <details className="border border-border rounded-md overflow-hidden mb-9 group">
        <summary className="list-none cursor-pointer w-full flex items-center justify-between px-4 h-11 text-[13px] hover:bg-surface-2 transition-colors">
          <span className="inline-flex items-center gap-2 text-ink-dim">
            <Icon name="settings" size={14} />
            {t("new.advanced")}
            <span className="text-ink-faint font-mono text-[11px]">{t("new.advancedHint")}</span>
          </span>
          <Icon name="chevron-down" size={14} className="text-ink-faint transition-transform [details[open]_&]:rotate-180" />
        </summary>
        <div className="p-5 border-t border-border bg-surface-2 grid gap-4">
          <label className="flex items-center justify-between gap-4 text-[13px] text-ink-dim">
            <span>
              <span className="font-mono text-[12px] text-ink">--yes</span> {t("new.skipIntent")}
            </span>
            <input type="checkbox" checked={skipIntent} onChange={(e) => setSkipIntent(e.target.checked)} />
          </label>
          <label className="flex items-center justify-between gap-4 text-[13px] text-ink-dim">
            <span>
              <span className="font-mono text-[12px] text-ink">--fresh</span> {t("new.ignoreCache")}
            </span>
            <input type="checkbox" checked={fresh} onChange={(e) => setFresh(e.target.checked)} />
          </label>
        </div>
      </details>

      {startError && (
        <div className="mb-5 rounded-md border border-danger/30 bg-danger-soft px-4 py-3 text-[13px] text-danger">
          {t("new.startFailed", { error: startError })}
        </div>
      )}

      <footer className="flex items-center justify-between gap-6 flex-wrap">
        <div className="text-[12.5px] text-ink-faint inline-flex items-center gap-2">
          <Icon name={visibilityForRole(role) === "public" ? "globe" : "lock"} size={14} />
          {isFree ? t("new.visPublic") : t("new.visPrivate")}
        </div>
        <div className="flex items-center gap-3">
          <Link to="/" className="h-11 px-5 text-[14.5px] inline-flex items-center justify-center rounded-lg text-ink-dim hover:text-ink hover:bg-surface-2">
            {t("common.cancel")}
          </Link>
          <Button variant="accent" size="lg" iconRight="arrow-right" disabled={!canStart} onClick={onStart}>
            {starting ? t("new.starting") : t("new.runBtn")}
          </Button>
        </div>
      </footer>
    </main>
  );
}
